import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { ChartBar, Clock, Warning, Timer, TrendUp } from "@phosphor-icons/react";

const fmtDur = (m) => {
  if (m === null || m === undefined) return "—";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), mm = m % 60;
  return h < 24 ? `${h}h ${mm}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
};

export default function SoysosReports() {
  const { data: r } = useQuery({ queryKey: ["sos-reports"], queryFn: async () => (await api.get("/soysos/reports")).data, refetchInterval: 10000 });
  const maxTop = Math.max(1, ...(r?.top_types || []).map((t) => t.count));

  return (
    <Page>
      <PageHeader title="Reports" subtitle="Request volume, resolution speed and how long issues stay open." />
      {!r ? <div className="text-muted-foreground">Loading…</div> : (
        <>
          <div className="grid grid-cols-4 gap-3 mb-6">
            <KPI label="Total requests" value={r.total} icon={ChartBar} />
            <KPI label="Avg resolution time" value={fmtDur(r.resolution_time_minutes.avg)} sub={`median ${fmtDur(r.resolution_time_minutes.median)}`} icon={Timer} />
            <KPI label="Avg time open" value={fmtDur(r.outstanding_time_minutes.avg)} sub={`oldest ${fmtDur(r.outstanding_time_minutes.max)}`} icon={Clock} />
            <KPI label="SLA breach rate" value={`${r.sla_breach_rate}%`} icon={Warning} tone={r.sla_breach_rate > 0 ? "danger" : "ok"} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-card rounded-lg border border-border p-5" data-testid="report-top-types">
              <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><TrendUp size={18} className="text-primary" /> Top 10 requested issues</h3>
              <div className="space-y-2.5">
                {r.top_types.map((t, i) => (
                  <div key={t.name} className="flex items-center gap-3">
                    <span className="text-[12px] text-muted-foreground w-5">{i + 1}.</span>
                    <span className="text-[13px] w-44 truncate">{t.name}</span>
                    <div className="flex-1 h-2.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(t.count / maxTop) * 100}%` }} /></div>
                    <span className="text-[12px] font-medium w-6 text-right">{t.count}</span>
                  </div>
                ))}
                {!r.top_types.length && <div className="text-muted-foreground text-sm py-6 text-center">No requests yet.</div>}
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-card rounded-lg border border-border p-5" data-testid="report-oldest">
                <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Clock size={18} className="text-amber-600" /> Longest outstanding</h3>
                <div className="space-y-2">
                  {r.oldest_open.slice(0, 6).map((t) => (
                    <div key={t.id} className="flex items-center justify-between text-[13px]">
                      <span className="truncate">{t.request_type} <span className="text-muted-foreground">· Room {t.room_number}</span></span>
                      <span className={`font-medium ${t.sla_breached ? "text-red-600" : "text-foreground"}`}>{fmtDur(t.age_minutes)}</span>
                    </div>
                  ))}
                  {!r.oldest_open.length && <div className="text-muted-foreground text-sm py-4 text-center">Nothing open — nice.</div>}
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-5" data-testid="report-slowest">
                <h3 className="font-heading font-semibold mb-3">Slowest to resolve (by type)</h3>
                <div className="space-y-2">
                  {r.slowest_request_types.map((t) => (
                    <div key={t.name} className="flex items-center justify-between text-[13px]">
                      <span className="truncate">{t.name}</span>
                      <span className="text-muted-foreground">{fmtDur(t.avg_minutes)} avg</span>
                    </div>
                  ))}
                  {!r.slowest_request_types.length && <div className="text-muted-foreground text-sm py-4 text-center">No resolved requests yet.</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-5 mt-4" data-testid="report-departments">
            <h3 className="font-heading font-semibold mb-3">By department</h3>
            <div className="flex flex-wrap gap-2">
              {r.by_department.map((d) => (
                <span key={d.department} className="text-[12.5px] px-3 py-1.5 rounded-full bg-secondary border border-border">{d.department}: <b>{d.count}</b></span>
              ))}
            </div>
          </div>
        </>
      )}
    </Page>
  );
}

function KPI({ label, value, sub, icon: Icon, tone }) {
  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11.5px] text-muted-foreground">{label}</span>
        <Icon size={16} className={tone === "danger" ? "text-red-500" : "text-primary"} />
      </div>
      <div className="text-2xl font-heading font-semibold mt-2">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
